import { useState, useCallback } from 'react'
import { useDropzone, type FileRejection } from 'react-dropzone'

interface UploadedVideo {
  file: File
  progress: number
  status: 'pending' | 'uploading' | 'complete' | 'error'
  error?: string
  videoId?: string
}

interface VideoUploadProps {
  videos: UploadedVideo[]
  onVideosChange: (videos: UploadedVideo[]) => void
  maxVideos?: number
  maxSizeMB?: number
  disabled?: boolean
  /** When true, renders without the outer card wrapper (for embedding in grid layouts) */
  compact?: boolean
}

const SUPPORTED_VIDEO_TYPES = {
  'video/mp4': ['.mp4'],
  'video/webm': ['.webm'],
  'video/quicktime': ['.mov'],
}

export default function VideoUpload({
  videos,
  onVideosChange,
  maxVideos = 3,
  maxSizeMB = 500,
  disabled = false,
  compact = false,
}: VideoUploadProps) {
  const [dragError, setDragError] = useState<string | null>(null)

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      setDragError(null)

      // Handle rejected files
      if (rejectedFiles.length > 0) {
        const rejection = rejectedFiles[0]
        if (rejection.errors[0]?.code === 'file-too-large') {
          setDragError(`File too large. Maximum size is ${maxSizeMB}MB`)
        } else if (rejection.errors[0]?.code === 'file-invalid-type') {
          setDragError('Unsupported format. Use MP4, WebM, or MOV')
        }
        return
      }

      // Check remaining slots
      const remainingSlots = maxVideos - videos.length
      if (remainingSlots <= 0) {
        setDragError(`Maximum ${maxVideos} videos allowed`)
        return
      }

      // Add new videos
      const newVideos = acceptedFiles.slice(0, remainingSlots).map((file) => ({
        file,
        progress: 0,
        status: 'pending' as const,
      }))

      onVideosChange([...videos, ...newVideos])
    },
    [videos, onVideosChange, maxVideos, maxSizeMB]
  )

  const removeVideo = (index: number) => {
    const newVideos = videos.filter((_, i) => i !== index)
    onVideosChange(newVideos)
  }

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: SUPPORTED_VIDEO_TYPES,
    maxFiles: maxVideos - videos.length,
    maxSize: maxSizeMB * 1024 * 1024,
    noClick: true,
    noKeyboard: true,
    disabled: disabled || videos.length >= maxVideos,
  })

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  // Compact mode: just the drop zone and video list, no outer wrapper/header
  if (compact) {
    return (
      <div className="space-y-2">
        {/* Drop Zone */}
        {videos.length === 0 && !disabled && (
          <div
            {...getRootProps()}
            className={`
              border border-dashed rounded-md bg-linear-bg p-4 flex flex-col items-center justify-center cursor-pointer transition-colors group
              ${isDragActive ? 'border-linear-accent bg-linear-accent/5' : 'border-linear-border hover:border-linear-text-muted'}
            `}
            onClick={open}
          >
            <input {...getInputProps()} />
            <svg
              className="w-6 h-6 text-linear-text-muted group-hover:text-linear-text-secondary mb-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
            <p className="text-xs text-linear-text-muted">
              {isDragActive ? 'Drop video here' : 'Upload screen recordings'}
            </p>
            <p className="text-[10px] text-linear-text-muted opacity-60 mt-1">Up to {maxSizeMB}MB</p>
          </div>
        )}

        {/* Add more button when videos exist */}
        {videos.length > 0 && videos.length < maxVideos && !disabled && (
          <button
            type="button"
            onClick={open}
            className="w-full border border-dashed border-linear-border rounded-md bg-linear-bg p-2 text-center hover:border-linear-text-muted transition-colors cursor-pointer"
          >
            <span className="text-xs text-linear-text-muted">+ Add more videos</span>
          </button>
        )}

        {/* Error Message */}
        {dragError && (
          <div className="text-xs text-error">{dragError}</div>
        )}

        {/* Uploaded Videos List */}
        {videos.length > 0 && (
          <div className="space-y-2">
            {videos.map((video, index) => (
              <div
                key={index}
                className="p-2 bg-linear-bg border border-linear-border rounded flex items-center justify-between"
              >
                <div className="flex items-center space-x-2 flex-1 min-w-0">
                  {video.status === 'complete' ? (
                    <svg className="w-4 h-4 text-success flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : video.status === 'error' ? (
                    <svg className="w-4 h-4 text-error flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  ) : video.status === 'uploading' ? (
                    <div className="spinner spinner-sm flex-shrink-0"></div>
                  ) : (
                    <svg className="w-4 h-4 text-linear-text-muted flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-linear-text truncate">{video.file.name}</p>
                    <p className="text-[10px] text-linear-text-muted">{formatFileSize(video.file.size)}</p>
                  </div>
                </div>

                {/* Progress Bar for uploading */}
                {video.status === 'uploading' && (
                  <div className="w-16 mx-2">
                    <div className="progress-bar h-1">
                      <div
                        className="progress-bar-fill"
                        style={{ width: `${video.progress}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                {/* Error Message */}
                {video.status === 'error' && video.error && (
                  <span className="text-[10px] text-error mx-2">{video.error}</span>
                )}

                {/* Remove Button */}
                {!disabled && video.status !== 'uploading' && (
                  <button
                    onClick={() => removeVideo(index)}
                    className="text-linear-text-muted hover:text-error transition-colors flex-shrink-0"
                    title="Remove video"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
            <p className="text-[10px] text-linear-text-muted">
              {videos.length} of {maxVideos} videos {videos.some(v => v.status === 'pending') && '(will upload after generation)'}
            </p>
          </div>
        )}
      </div>
    )
  }

  // Default (non-compact) mode: full card wrapper with header
  return (
    <div className="mb-6 p-4 bg-linear-bg-card rounded">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-medium text-linear-text">Video Reference</h3>
          <p className="text-xs text-linear-text-muted mt-0.5">
            Add screen recordings to view alongside your test cases
          </p>
        </div>
        {videos.length < maxVideos && !disabled && (
          <button
            type="button"
            onClick={open}
            className="btn btn-secondary text-sm py-1.5"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
            {videos.length === 0 ? 'Upload Video' : 'Add More'}
          </button>
        )}
      </div>

      {/* Drop Zone - only show when no videos uploaded yet */}
      {videos.length === 0 && !disabled && (
        <div
          {...getRootProps()}
          className={`
            border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all
            ${isDragActive ? 'border-linear-accent bg-linear-accent/5' : 'border-linear-border hover:border-linear-accent/50'}
          `}
          onClick={open}
        >
          <input {...getInputProps()} />
          <svg
            className="w-10 h-10 text-linear-text-muted mx-auto mb-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
          <p className="text-sm text-linear-text-secondary mb-1">
            {isDragActive ? 'Drop video here' : 'Drag & drop video or click to upload'}
          </p>
          <p className="text-xs text-linear-text-muted">
            MP4, WebM, MOV (max {maxSizeMB}MB, up to {maxVideos} videos)
          </p>
        </div>
      )}

      {/* Error Message */}
      {dragError && (
        <div className="mt-2 text-sm text-error">{dragError}</div>
      )}

      {/* Uploaded Videos List */}
      {videos.length > 0 && (
        <div className="space-y-2">
          {videos.map((video, index) => (
            <div
              key={index}
              className="p-3 bg-linear-bg-input border border-linear-border rounded flex items-center justify-between"
            >
              <div className="flex items-center space-x-3 flex-1 min-w-0">
                {video.status === 'complete' ? (
                  <svg className="w-5 h-5 text-success flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : video.status === 'error' ? (
                  <svg className="w-5 h-5 text-error flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : video.status === 'uploading' ? (
                  <div className="spinner spinner-sm flex-shrink-0"></div>
                ) : (
                  <svg className="w-5 h-5 text-linear-text-muted flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-linear-text truncate">{video.file.name}</p>
                  <p className="text-xs text-linear-text-muted">{formatFileSize(video.file.size)}</p>
                </div>
              </div>

              {/* Progress Bar for uploading */}
              {video.status === 'uploading' && (
                <div className="w-24 mx-4">
                  <div className="progress-bar h-1.5">
                    <div
                      className="progress-bar-fill"
                      style={{ width: `${video.progress}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-linear-text-muted text-center mt-1">{video.progress}%</p>
                </div>
              )}

              {/* Error Message */}
              {video.status === 'error' && video.error && (
                <span className="text-xs text-error mx-4">{video.error}</span>
              )}

              {/* Remove Button */}
              {!disabled && video.status !== 'uploading' && (
                <button
                  onClick={() => removeVideo(index)}
                  className="text-linear-text-muted hover:text-error transition-colors flex-shrink-0"
                  title="Remove video"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          ))}
          <p className="text-xs text-linear-text-muted">
            {videos.length} of {maxVideos} videos {videos.some(v => v.status === 'pending') && '(will upload after generation)'}
          </p>
        </div>
      )}
    </div>
  )
}

export type { UploadedVideo }
